import { useParams, Link } from 'react-router-dom';
import { ArrowLeft, Shield, FileText, RefreshCw, Mail, Phone, MapPin, Building } from 'lucide-react';
import { Button } from '@/components/ui/button';

const COMPANY_INFO = {
  name: 'Ncriptech Global Private Limited',
  address: '2nd Floor, Rainbow Apartment, Kanaipur, Konnagar, West Bengal-712246',
  email: 'info@ncriptech.com',
  phone: '9836807060',
  website: 'cloudcampus.co.in'
};

const StaticLegalPage = () => {
  const { pageType } = useParams();

  const pages = {
    'privacy-policy': {
      title: 'Privacy Policy',
      icon: <Shield className="h-6 w-6 text-blue-600" />,
      content: `
## Privacy Policy

**Effective Date: January 2026**

**${COMPANY_INFO.name}** ("we", "us", or "our") operates the Cloud Campus school management platform. This Privacy Policy explains how we collect, use, disclose, and safeguard your information.

### 1. Information We Collect

#### Personal Information
- **Account Information:** Name, email address, phone number, role (admin, teacher, student, parent)
- **Student Information:** Date of birth, class details, academic records, attendance data
- **Parent/Guardian Information:** Contact details, relationship to student
- **Payment Information:** Transaction details, payment history (processed securely via Razorpay)

#### Automatically Collected Information
- Device information, IP address, browser type
- Usage data and access logs
- Cookies for session management

### 2. How We Use Your Information

We use the collected information to:
- Provide and maintain the school management services
- Process fee payments and generate receipts
- Send notifications about attendance, exams, and school events
- Improve our services and user experience
- Comply with legal obligations

### 3. Information Sharing

We do **not** sell, trade, or rent your personal information. We may share information with:
- **Schools:** Data is shared with the respective school administration
- **Payment Processors:** Razorpay for secure payment processing
- **Legal Authorities:** When required by law or to protect our rights

### 4. Data Security

We implement industry-standard security measures:
- Encrypted data transmission (HTTPS/SSL)
- Secure cloud storage with access controls
- Regular security audits and updates

### 5. Data Retention

- Active account data is retained while the account is in use
- Deleted accounts data is purged within 90 days
- Legal/compliance data may be retained as required by law

### 6. Your Rights

You have the right to:
- Access your personal data
- Request correction of inaccurate data
- Request deletion of your data (subject to legal requirements)
- Withdraw consent for marketing communications

### 7. Children's Privacy

Our service is designed for educational institutions. Student data is collected and managed by schools in compliance with applicable child protection laws.

### 8. Changes to This Policy

We may update this Privacy Policy periodically. Users will be notified of significant changes via email or in-app notification.

### 9. Contact Us

For privacy-related inquiries:

**${COMPANY_INFO.name}**  
${COMPANY_INFO.address}  
Email: ${COMPANY_INFO.email}  
Phone: +91 ${COMPANY_INFO.phone}
      `
    },
    'terms-of-use': {
      title: 'Terms of Use',
      icon: <FileText className="h-6 w-6 text-green-600" />,
      content: `
## Terms of Use

**Effective Date: January 2026**

Welcome to Cloud Campus. By accessing or using our school management platform, you agree to be bound by these Terms of Use.

### 1. Acceptance of Terms

By creating an account or using Cloud Campus services, you acknowledge that you have read, understood, and agree to these Terms. If you do not agree, please do not use our services.

### 2. Description of Service

Cloud Campus is a SaaS-based school management platform that provides:
- Student information management
- Fee collection and accounting
- Attendance tracking
- Exam and result management
- Communication tools
- And other educational administration features

### 3. User Accounts

#### Registration
- You must provide accurate and complete information
- You are responsible for maintaining account security
- One account per user; sharing credentials is prohibited

#### Account Types
- **School Admin:** Full administrative access
- **Teacher:** Class and student management
- **Student:** Personal dashboard access
- **Parent:** Child monitoring access

### 4. Acceptable Use

You agree NOT to:
- Use the service for any unlawful purpose
- Attempt to gain unauthorized access to any system
- Upload malicious code or content
- Impersonate another person or entity
- Interfere with or disrupt the service
- Scrape or harvest data without permission

### 5. Subscription and Payments

- Schools subscribe to plans (Basic, Standard, Premium, Enterprise)
- Payments are processed securely via Razorpay
- Subscription renewal is automatic unless cancelled
- See Refund Policy for cancellation terms

### 6. Intellectual Property

- Cloud Campus platform and its original content are owned by ${COMPANY_INFO.name}
- Schools retain ownership of their uploaded data
- You may not copy, modify, or distribute our software

### 7. Data and Privacy

Your use of Cloud Campus is also governed by our Privacy Policy. By using the service, you consent to our data practices.

### 8. Service Availability

- We strive for 99.9% uptime but do not guarantee uninterrupted service
- Scheduled maintenance will be communicated in advance
- We are not liable for service interruptions beyond our control

### 9. Limitation of Liability

To the maximum extent permitted by law:
- We are not liable for indirect, incidental, or consequential damages
- Our total liability shall not exceed the amount paid by you in the last 12 months

### 10. Termination

- You may terminate your account at any time
- We may suspend or terminate accounts for violation of these terms
- Upon termination, your data may be deleted after a retention period

### 11. Governing Law

These terms are governed by the laws of India. Any disputes shall be resolved in the courts of Kolkata, West Bengal.

### 12. Changes to Terms

We may modify these terms at any time. Continued use after changes constitutes acceptance.

### 13. Contact

For questions about these Terms:

**${COMPANY_INFO.name}**  
Email: ${COMPANY_INFO.email}  
Phone: +91 ${COMPANY_INFO.phone}
      `
    },
    'refund-policy': {
      title: 'Refund Policy',
      icon: <RefreshCw className="h-6 w-6 text-orange-600" />,
      content: `
## Refund Policy

**Effective Date: January 2026**

This Refund Policy applies to all payments made through the Cloud Campus platform.

### 1. Refund Eligibility

#### For School Subscriptions (SaaS)

| Scenario | Refund | Timeline |
|----------|--------|----------|
| Cancellation within 7 days of purchase | Full refund | Within 7 days |
| Technical issues preventing service use | Pro-rated refund | Case by case |
| Service not as described | Full refund | Within 14 days |
| Cancellation after 7 days | No refund | - |

#### For Student Fee Payments

Student fee refunds are governed by each school's individual policy. Contact your school administration for fee-related refunds.

### 2. Refund Request Process

**Step 1:** Submit a refund request via email to ${COMPANY_INFO.email}

**Step 2:** Include the following information:
- School/Account name
- Transaction ID or receipt number
- Reason for refund request
- Supporting documentation (if applicable)

**Step 3:** Our team will review your request within 3-5 business days

**Step 4:** If approved, refund will be processed within 5-7 business days

### 3. Refund Method

- Refunds will be credited to the **original payment method**
- Bank transfers: 5-7 business days
- UPI/Wallet: 2-3 business days
- Card payments: 5-10 business days (depending on bank)

### 4. Non-Refundable Items

The following are **not eligible** for refund:
- Setup or onboarding fees (if any)
- Custom development charges
- Third-party integration fees
- Subscription time already used (for pro-rated refunds)

### 5. Disputed Transactions

If you believe a transaction is unauthorized:
1. Contact us immediately at ${COMPANY_INFO.email}
2. We will investigate and respond within 48 hours
3. File a dispute with your bank if needed

### 6. Cancellation vs Refund

- **Cancellation:** Stops future billing; current period remains active
- **Refund:** Returns payment for service not used/delivered

### 7. Contact for Refunds

**${COMPANY_INFO.name}**  
Address: ${COMPANY_INFO.address}  
Email: ${COMPANY_INFO.email}  
Phone: +91 ${COMPANY_INFO.phone}

**Refund Request Email Format:**
\`\`\`
Subject: Refund Request - [School Name] - [Transaction ID]

Body:
- School Name:
- Contact Person:
- Phone Number:
- Transaction ID:
- Amount:
- Date of Payment:
- Reason for Refund:
\`\`\`
      `
    },
    'contact': {
      title: 'Contact Us',
      icon: <Mail className="h-6 w-6 text-purple-600" />,
      content: `
## Contact Us

We're here to help! Reach out to us through any of the following channels.

### ${COMPANY_INFO.name}

---

### 📍 Office Address

**${COMPANY_INFO.address}**

---

### 📧 Email

**General Inquiries:** ${COMPANY_INFO.email}

**Support:** support@cloudcampus.co.in

**Sales:** sales@cloudcampus.co.in

---

### 📞 Phone

**+91 ${COMPANY_INFO.phone}**

*Available: Monday to Saturday, 10:00 AM - 6:00 PM IST*

---

### 🌐 Website

**www.${COMPANY_INFO.website}**

---

### Business Hours

| Day | Hours |
|-----|-------|
| Monday - Friday | 10:00 AM - 6:00 PM |
| Saturday | 10:00 AM - 2:00 PM |
| Sunday | Closed |

---

### Support Response Time

| Priority | Response Time |
|----------|---------------|
| Critical (Service Down) | Within 2 hours |
| High (Major Issue) | Within 4 hours |
| Normal | Within 24 hours |
| Low (Feature Request) | Within 48 hours |

---

### Follow Us

Connect with us on social media for updates and tips:
- LinkedIn: Ncriptech Global
- Twitter: @cloudcampus_in

---

### Feedback

We value your feedback! Help us improve Cloud Campus by sharing your suggestions at feedback@cloudcampus.co.in
      `
    }
  };

  const pageData = pages[pageType];

  if (!pageData) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-blue-900 to-slate-900 flex items-center justify-center">
        <div className="text-center text-white">
          <h1 className="text-2xl font-bold mb-4">Page Not Found</h1>
          <Link to="/">
            <Button>Go Home</Button>
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-blue-900 to-slate-900">
      {/* Header */}
      <header className="bg-white/10 backdrop-blur-sm border-b border-white/10 sticky top-0 z-10">
        <div className="max-w-4xl mx-auto px-4 py-4 flex items-center gap-4">
          <Link to="/">
            <Button variant="ghost" size="icon" className="text-white hover:bg-white/10">
              <ArrowLeft className="h-5 w-5" />
            </Button>
          </Link>
          <div className="flex items-center gap-3">
            <div className="p-2 bg-white/10 rounded-lg">
              {pageData.icon}
            </div>
            <div>
              <h1 className="text-xl font-semibold text-white">{pageData.title}</h1>
              <p className="text-sm text-white/60">Cloud Campus</p>
            </div>
          </div>
        </div>
      </header>

      {/* Content */}
      <main className="max-w-4xl mx-auto px-4 py-8">
        <div className="bg-white rounded-xl shadow-2xl p-6 md:p-10">
          <div className="prose prose-blue max-w-none prose-headings:text-gray-800 prose-p:text-gray-600 prose-li:text-gray-600 prose-table:text-sm">
            {pageData.content.split('\n').map((line, i) => {
              if (line.startsWith('## ')) {
                return <h2 key={i} className="text-2xl font-bold text-gray-800 mt-8 mb-4">{line.replace('## ', '')}</h2>;
              } else if (line.startsWith('### ')) {
                return <h3 key={i} className="text-xl font-semibold text-gray-700 mt-6 mb-3">{line.replace('### ', '')}</h3>;
              } else if (line.startsWith('#### ')) {
                return <h4 key={i} className="text-lg font-medium text-gray-700 mt-4 mb-2">{line.replace('#### ', '')}</h4>;
              } else if (line.startsWith('- ')) {
                return <li key={i} className="ml-4 text-gray-600">{line.replace('- ', '')}</li>;
              } else if (line.startsWith('| ')) {
                return null; // Tables handled separately
              } else if (line.startsWith('**') && line.endsWith('**')) {
                return <p key={i} className="font-semibold text-gray-700">{line.replace(/\*\*/g, '')}</p>;
              } else if (line.trim() === '---') {
                return <hr key={i} className="my-6 border-gray-200" />;
              } else if (line.trim()) {
                return <p key={i} className="text-gray-600 mb-2">{line}</p>;
              }
              return null;
            })}
          </div>
        </div>
      </main>

      {/* Footer */}
      <footer className="bg-black/20 backdrop-blur-sm border-t border-white/10 mt-8 py-8">
        <div className="max-w-4xl mx-auto px-4">
          <div className="grid md:grid-cols-3 gap-6 text-white/80 text-sm">
            <div>
              <h4 className="font-semibold text-white mb-2">Cloud Campus</h4>
              <p>Modern School Management System</p>
            </div>
            <div>
              <h4 className="font-semibold text-white mb-2">Legal</h4>
              <div className="space-y-1">
                <Link to="/page/privacy-policy" className="block hover:text-white">Privacy Policy</Link>
                <Link to="/page/terms-of-use" className="block hover:text-white">Terms of Use</Link>
                <Link to="/page/refund-policy" className="block hover:text-white">Refund Policy</Link>
              </div>
            </div>
            <div>
              <h4 className="font-semibold text-white mb-2">Contact</h4>
              <p>{COMPANY_INFO.email}</p>
              <p>+91 {COMPANY_INFO.phone}</p>
            </div>
          </div>
          <div className="text-center text-white/60 text-sm mt-8 pt-6 border-t border-white/10">
            <p>© {new Date().getFullYear()} {COMPANY_INFO.name}. All rights reserved.</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default StaticLegalPage;
